import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks';
import { LatLong } from '../../../models/Geometries';
import { Layer, Marker, Source } from 'react-map-gl';
import { useEffect, useMemo, useState } from 'react';
import {
  calculateArea,
  generateRectangle,
  getTopRightCoordinate,
} from '../../../utils/GeometryUtils';
import { Button, Modal } from 'antd';
import { deleteAOI, setAddingAOI } from '../../../store/slice/mapAOISlice';
import Icon from '@ant-design/icons';
import { ReactComponent as Remove } from '../../../res/svg/remove-outlined.svg';
import './MapAOI.less';
import { removeFeatureSelected } from '../../../store/slice/mapLayersSlice';
import { Feature } from '../../../models/Geoserver';
import { getAOIImage } from '../../../store/services/map/map.service';
import { adjuntosEndPoint } from '../../../utils/endPoints';
import { TokenService } from '../../../utils/axios.interceptor';

interface Props {
  mousePosition: LatLong | undefined;
}

function MapAOI({ mousePosition }: Props) {
  const dispatch = useAppDispatch();
  const firstPoint = useAppSelector(state => state.mapAOI.firstPoint);
  const secondPoint = useAppSelector(state => state.mapAOI.secondPoint);
  const addingAOI = useAppSelector(state => state.mapAOI.addingAOI);
  const aoi = useAppSelector(state => state.mapAOI.aoi);
  const aoiVisible = useAppSelector(state => state.mapAOI.aoiVisible);
  const isSelected = useAppSelector(state => state.mapAOI.aoiSelected);
  const [loadingToken, setLoadingToken] = useState(false);

  const rectangle = useMemo(() => {
    if (addingAOI && firstPoint && mousePosition) {
      return generateRectangle(mousePosition, firstPoint);
    } else if (firstPoint && secondPoint) {
      return generateRectangle(firstPoint, secondPoint);
    }
  }, [firstPoint, secondPoint, mousePosition, addingAOI]);

  useEffect(() => {
    if(aoi && !addingAOI && aoiVisible){
      setLoadingToken(true);
      TokenService.forceRefresh().then(() => {
        setLoadingToken(false);
      })
    }
  }, [aoi, aoiVisible, addingAOI])

  const getLayerImage = () => {
    if (aoi?.adjunto?.urlBuffer) {
      return `${process.env.REACT_APP_API_URL}${aoi.adjunto.urlBuffer}`;
    }
  };

  const imageCoords = aoi ? (aoi.area.coordinates as number[][][])[0].slice(0, 4) : [];

  if (loadingToken || !rectangle || !aoiVisible || (!addingAOI && !aoi)) {
    return null;
  }

  return (
    <div>
      <Marker longitude={getTopRightCoordinate({
        type: 'Feature',
        geometry: rectangle as any,
      } as Feature)[0]} latitude={getTopRightCoordinate({
        type: 'Feature',
        geometry: rectangle as any,
      } as Feature)[1]}>
        <div
          className={
            'at-map-cm-geometries__delete-container ' +
            (isSelected ? 'selected' : '')
          }
        >
          <Button
            className={'at-map-cm-geometries__delete '}
            size='large'
            shape='round'
            onClick={(e) => {
              dispatch(deleteAOI());
              e.stopPropagation();
            }}
            type={'default'}
            icon={<Icon component={Remove} />}
          />
        </div>
      </Marker>
      <Source id={'AOISOURCE'}
              type='geojson' data={{
        type: 'Feature',
        geometry: rectangle as any,
        properties: {},
      }}>
        <Layer id={'AOISOURCE_polygon'} type={'line'} paint={{
          'line-color': isSelected ? '#F03C5A' : '#B2D235',
          'line-width': addingAOI ? 2 : 4,
          'line-dasharray': addingAOI ? [0.5, 0.5] : [1],
        }} beforeId={'order-polygon'}
        />
        <Layer id={'AOISOURCE_polygon_black'} type={'line'} paint={{
          'line-color': 'black',
          'line-width': 6,
          'line-opacity': addingAOI ? 0 : 1,
        }} beforeId={'AOISOURCE_polygon'}
        />
        <Layer id={'aoi_click_area'} type={'line'} paint={{
          'line-color': 'rgba(0,0,0,0)',
          'line-width': 12,
        }} beforeId={'order-line'}
        />
        <Layer id={'AOISOURCE_polygon_fill'} type={'fill'} paint={{
          'fill-color': addingAOI && calculateArea(rectangle.coordinates[0])
          > parseInt(process.env.REACT_APP_MAX_AOI_AREA_KM2 as string) ? 'red' : '#3BB478',
          'fill-opacity': addingAOI ? 0.2 : 0,
        }} beforeId={'order-polygon'}
        />
      </Source>
      {
        aoi?.adjunto?.urlBuffer ? (
          <Source
            key={aoi.createdAt}
            id={'source_aoi_' + aoi.createdAt}
            type='image'
            coordinates={imageCoords}
            url={getLayerImage()}
          >
            <Layer beforeId={'order-raster'} id={'raster-layer-aoi-' + aoi.createdAt} type={'raster'} />
          </Source>
        ) : null
      }
    </div>
  );
}

export default MapAOI;
